import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Check, Wand2, X, Download } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import GenerateButton, { useCost, Money } from "@/components/GenerateButton";
import PromptBox from "@/components/PromptBox";
import ContinuityPicker from "@/components/cgrender/ContinuityPicker";
import ImageViewer from "@/components/ImageViewer";
import { api, errMsg, mediaUrl, saveAs } from "@/lib/api";
import { tagOf } from "@/lib/useCpSession";

export const CP_IMAGE_PREFIX = "Use reference frame named '_Keyframe' as marking guide and edit the image named '_for_cleanup' respectively by matching exact starting name (except _Keyframe and _cleanup). Apply the following edit:";
const MANAGED = /@\S+_(?:Keyframe|for_cleanup)(?![\w.-]) ?/g;

// Every image model that edits from reference images (exact colour + reference following).
const pickModels = (models) => models.filter((m) => m.section === "image" && m.available && m.capabilities?.image_edit);

const K_PX = { "1K": 1024, "2K": 2048, "4K": 4096 };
const ratio = (a) => { const [w, h] = String(a).split(":").map(Number); return w && h ? w / h : null; };
export const nearestAspect = (list, info) => {
  if (list.includes("auto")) return "auto";
  const r = info?.width && info?.height ? info.width / info.height : 16 / 9;
  return list.filter(ratio).sort((a, b) => Math.abs(ratio(a) - r) - Math.abs(ratio(b) - r))[0];
};
export const nearestRes = (list, info) => {
  const long = info?.width && info?.height ? Math.max(info.width, info.height) : 1920;
  const s = [...list].filter((r) => K_PX[r]).sort((a, b) => K_PX[a] - K_PX[b]);
  return s.find((r) => K_PX[r] >= long) || s[s.length - 1] || list[0];
};

const useGen = (id) => {
  const [g, setG] = useState(null);
  useEffect(() => {
    let live = true, t;
    const tick = () => api.get(`/generations/${id}`).then((r) => { if (!live) return; setG(r.data); if (r.data.status === "running") t = setTimeout(tick, 3000); })
      .catch(() => { if (live) t = setTimeout(tick, 6000); });
    tick();
    return () => { live = false; clearTimeout(t); };
  }, [id]);
  return g;
};

const Candidate = ({ job, onUse, onDismiss, useLabel = "Use It", nameFor }) => {
  const g = useGen(job.id);
  const [using, setUsing] = useState(-1);
  const [view, setView] = useState(null);
  const use = async (i) => { setUsing(i); try { await onUse(g, i, job); } finally { setUsing(-1); } };
  const outs = g?.output_urls || [];
  return (
    <div className="rounded-lg border border-zinc-800 bg-[#0B0F16] p-2" data-testid={`cp-candidate-${job.id}`}>
      <div className="flex items-center justify-between text-[11px] font-mono text-zinc-400 mb-1.5">
        <span>frame #{job.frame}</span>
        <button type="button" onClick={() => onDismiss(job)} className="hover:text-red-400" data-testid={`cp-candidate-dismiss-${job.id}`}><X className="w-3.5 h-3.5" /></button>
      </div>
      {job.edit && <p className="text-[10px] text-zinc-500 line-clamp-2 mb-1.5" title={job.edit}>{job.edit}</p>}
      {!g || g.status === "running" ? <div className="h-24 flex items-center justify-center gap-2 text-xs text-zinc-400"><Loader2 className="w-4 h-4 animate-spin text-[#41a7f5]" />Creating…</div>
        : g.status === "failed" ? <div className="text-xs text-red-300 p-2" data-testid={`cp-candidate-failed-${job.id}`}>{String(g.error || "Generation failed")}</div>
        : outs.map((u, i) => (
          <div key={u} className="mb-1.5 last:mb-0">
            <button type="button" onClick={() => setView(mediaUrl(u))} className="block w-full cursor-zoom-in" title="Click to view full size" data-testid={`cp-candidate-view-${job.id}-${i}`}>
              <img src={mediaUrl(u)} alt="clean plate" className="w-full max-h-48 object-contain rounded bg-black" data-testid={`cp-candidate-img-${job.id}-${i}`} />
            </button>
            {nameFor && <button type="button" onClick={() => { const nm = nameFor(job).replace(/\.png$/, outs.length > 1 ? `_${i + 1}.png` : ".png"); saveAs(u, nm).catch(() => toast.error("Could not download that plate")); }}
              title={`Download as ${nameFor(job)}`} data-testid={`cp-candidate-download-${job.id}-${i}`}
              className="w-full h-8 mt-1 rounded-md border border-zinc-700 text-zinc-200 text-[11px] flex items-center justify-center gap-1.5 hover:border-[#1477d2]"><Download className="w-3.5 h-3.5 shrink-0" />Download</button>}
            <button type="button" onClick={() => use(i)} disabled={using >= 0 || job.used?.includes(i)} data-testid={`cp-use-it-${job.id}-${i}`}
              className="w-full h-9 mt-1 rounded-md bfx-gradient-bg text-white text-xs font-semibold flex items-center justify-center gap-1.5 disabled:opacity-50">
              {using === i ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}{job.used?.includes(i) ? "Locked" : useLabel}</button>
          </div>
        ))}
      <ImageViewer src={view} onClose={() => setView(null)} />
    </div>
  );
};

export const Candidates = ({ jobs = [], onUse, onDismiss, cols = "grid-cols-2", useLabel, nameFor }) => !jobs.length ? null : (
  <div className={`grid ${cols} gap-2`} data-testid="cp-candidates">{jobs.map((j) => <Candidate key={j.id} job={j} onUse={onUse} onDismiss={onDismiss} useLabel={useLabel} nameFor={nameFor} />)}</div>
);

export const ACT_IMAGE_PREFIX = "Use the reference frame named '_Keyframe' as the base image and the marked frame named '_for_cleanup' as the placement guide. Add a cinematic practical VFX action element exactly in the marked area, matching the frame's camera, lens, lighting, perspective, scale, motion blur and grain. Keep every character, the set and the composition identical outside the effect. Effect to add:";
const ACT_DEFAULT_EDIT = "a realistic practical action VFX element (impact, debris, sparks, smoke) in the marked area";
const DEFAULT_V = { title: "Create Clean Plate", noun: "clean plate", optional: false, danger: false, hideTemplate: false };

const KeyframePrompt = ({ k, value, onChange, children, v = DEFAULT_V }) => (
  <div className="rounded-xl border border-zinc-800 bg-[#0B0F16] p-2.5" data-testid={`cp-kf-prompt-${k.frame}`}>
    <div className="flex gap-2 mb-2">
      {[k.keyframe, k.cleanup].map((r) => <img key={r.id} src={mediaUrl(r.url)} alt="" className="h-14 w-20 object-cover rounded border border-zinc-800 bg-black" />)}
      <div className="flex-1 min-w-0">
        <div className="text-xs font-semibold text-white">Frame #{k.frame}</div>
        <div className="flex flex-wrap gap-1 mt-1" data-testid={`cp-kf-tags-${k.frame}`}>
          {[k.keyframe, k.cleanup].map((r) => <span key={r.id} className="ax-badge !text-[#64b7f6] !normal-case">@{tagOf(r)}</span>)}
        </div>
      </div>
    </div>
    <PromptBox value={value} onChange={onChange} testid={`cp-kf-edit-${k.frame}`} minRows={2} danger={v.danger}
      tags={[k.keyframe, k.cleanup, ...(k.cont || [])].map((r) => ({ tag: tagOf(r), ref: r }))}
      placeholder={v.optional ? `Optional — the action effect for frame #${k.frame}, e.g. bullet hit sparks on the wall, a fire burst behind the car` : `What to change in frame #${k.frame} — e.g. replace the backdrop with desert dunes, keep the actor`} className="ax-input bfx-input" />
    {children}
  </div>
);

const CP_CONT = "CONTINUITY REFERENCE @Continuity: this frame is from the same shot — match its colour grade, lighting, materials and look exactly. Take structure only from @Cleanup.";

export default function CleanPlateDialog({ open, onOpenChange, models, projectId, keyframes, st, patch, onUse, onStarted, info, onUploaded, variant, nameFor }) {
  const v = variant || DEFAULT_V;
  const list = useMemo(() => pickModels(models), [models]);
  const modelId = list.some((m) => m.id === st.img_model) ? st.img_model : list[0]?.id;
  const template = v.hideTemplate ? ACT_IMAGE_PREFIX : st.img_template ?? CP_IMAGE_PREFIX;
  const edits = st.kf_edits || {};
  const model = list.find((m) => m.id === modelId);
  const caps = model?.capabilities || {};
  const ars = caps.aspect_ratios || [], ress = caps.resolutions || [];
  const aspect = ars.includes(st.img_aspect) ? st.img_aspect : nearestAspect(ars, info);
  const resolution = ress.includes(st.img_res) ? st.img_res : nearestRes(ress, info);
  const settings = { aspect_ratio: aspect, resolution, num_images: 1, quality: (caps.qualities || []).includes("high") ? "high" : undefined, ...(v.danger ? { action_safe: true } : {}) };
  const one = useCost(modelId, settings, !!modelId);
  const usd = one == null ? null : one * keyframes.length;
  const missing = v.optional ? [] : keyframes.filter((k) => !(edits[k.frame] || "").trim());
  const baseReason = !model ? "No image edit model available" : "";
  const reason = !keyframes.length ? "No keyframes" : baseReason || (missing.length ? `Describe the edit for frame #${missing[0].frame}` : "");
  const maxCont = Math.max(0, (caps.max_images || 4) - 2);
  const locked = (st.plates || []).map((r) => ({ frame: Number((tagOf(r).match(/_(\d+)_(?:cleanplate|actionplate)/) || [])[1] ?? -1), ref: r }));
  const setCont = (frame, cont) => patch((s) => ({ keyframes: (s.keyframes || []).map((y) => (y.frame === frame ? { ...y, cont } : y)) }));

  const run = (list) => {
    const body = template.replace(MANAGED, "").trim();
    return Promise.all(list.map((k) => {
      const edit = (edits[k.frame] || "").trim(), cont = (k.cont || []).slice(0, maxCont);
      const line = cont.length ? ` ${CP_CONT.replace("@Continuity", cont.map((r) => `@${tagOf(r)}`).join(" ")).replace("@Cleanup", `@${tagOf(k.cleanup)}`)}` : "";
      return api.post("/generations", { project_id: projectId, model_id: modelId, section: "image", mode: "create",
        prompt: `@${tagOf(k.keyframe)} @${tagOf(k.cleanup)} ${body}${line} ${edit || (v.optional ? ACT_DEFAULT_EDIT : "")}`, settings, image_ids: [k.keyframe.id, k.cleanup.id, ...cont.map((r) => r.id)] }, { timeout: 180000 })
        .then(({ data }) => ({ id: data.id, frame: k.frame, edit }))
        .catch((e) => { toast.error(String(errMsg(e, `Could not start frame #${k.frame}`))); return null; });
    })).then((res) => {
      const jobs = res.filter(Boolean);
      if (jobs.length) { patch((s) => ({ image_jobs: [...jobs, ...(s.image_jobs || [])] })); onStarted?.(); }
    });
  };
  const go = () => {
    onOpenChange(false);
    toast.message(`Creating ${keyframes.length} ${v.noun}${keyframes.length > 1 ? "s" : ""} in the background`, { description: "Extra ones wait in a queue and start automatically as each finishes." });
    run(keyframes);
  };
  const goOne = (k) => {
    if (!v.optional && !(edits[k.frame] || "").trim()) { toast.error(`Describe the edit for frame #${k.frame}`); return; }
    toast.message(`Creating ${v.noun} for frame #${k.frame}`, { description: "Pick it below with USE IT." });
    run([k]);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bfx bg-[#070B14] border-[#1065c0]/50 text-zinc-100 max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="cp-image-dialog" aria-describedby={undefined}
        onEscapeKeyDown={(e) => { if (document.querySelector("[data-testid=image-viewer]")) e.preventDefault(); }}
        onPointerDownOutside={(e) => { if (document.querySelector("[data-testid=image-viewer]")) e.preventDefault(); }}>
        <DialogHeader><DialogTitle className="font-heading text-xl flex items-center gap-2"><Wand2 className="w-5 h-5 text-[#41a7f5]" />{v.title}</DialogTitle></DialogHeader>
        <div>
          <div className="text-xs text-zinc-300 mb-1.5">Image model</div>
          <div className="flex flex-wrap gap-1.5" data-testid="cp-image-models">
            {list.map((m) => <button key={m.id} type="button" onClick={() => patch({ img_model: m.id })} data-testid={`cp-image-model-${m.id}`}
              className={`ax-chip ${m.id === modelId ? "!border-[#1e97f3] text-white bg-[#0747a1]/30" : ""}`}>{m.name}</button>)}
          </div>
        </div>
        {!!ars.length && (
          <div data-testid="cp-image-aspects">
            <div className="text-xs text-zinc-300 mb-1.5">Aspect ratio <span className="text-zinc-500">· default matches your {info ? `${info.width}×${info.height}` : ""} source</span></div>
            <div className="flex flex-wrap gap-1.5">{ars.map((a) => <button key={a} type="button" onClick={() => patch({ img_aspect: a })} data-testid={`cp-image-aspect-${a}`}
              className={`ax-chip ${a === aspect ? "!border-[#1e97f3] text-white bg-[#0747a1]/30" : ""}`}>{a === "auto" ? "Auto (source)" : a}</button>)}</div>
          </div>
        )}
        {!!ress.length && (
          <div data-testid="cp-image-resolutions">
            <div className="text-xs text-zinc-300 mb-1.5">Resolution <span className="text-zinc-500">· max {ress[ress.length - 1]} for {model?.name}</span></div>
            <div className="flex flex-wrap gap-1.5">{ress.map((r) => <button key={r} type="button" onClick={() => patch({ img_res: r })} data-testid={`cp-image-res-${r}`}
              className={`ax-chip ${r === resolution ? "!border-[#1e97f3] text-white bg-[#0747a1]/30" : ""}`}>{r}</button>)}</div>
          </div>
        )}
        {!v.hideTemplate && <details className="rounded-lg border border-zinc-800 p-2.5" data-testid="cp-image-template">
          <summary className="text-xs text-zinc-300 cursor-pointer">Instruction template <span className="text-zinc-500">(sent with every frame)</span></summary>
          <div className="mt-2"><PromptBox value={template} onChange={(t) => patch({ img_template: t })} testid="cp-image-prefix" minRows={3} className="ax-input bfx-input text-xs text-zinc-300" /></div>
        </details>}
        <div className="space-y-2" data-testid="cp-kf-prompts">
          <div className="text-xs text-zinc-300">Edit per keyframe · {keyframes.length} keyframe{keyframes.length === 1 ? "" : "s"} → {keyframes.length} image{keyframes.length === 1 ? "" : "s"}</div>
          {keyframes.map((k) => (
            <KeyframePrompt key={k.frame} k={k} v={v} value={edits[k.frame] || ""} onChange={(v) => patch((s) => ({ kf_edits: { ...(s.kf_edits || {}), [k.frame]: v } }))}>
              <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0"><ContinuityPicker k={k} st={st} max={maxCont} stem={tagOf(k.keyframe).replace(/_\d+_Keyframe$/, "") || "shot"} locked={locked} jobs={st.image_jobs || []}
                  onUploaded={onUploaded} onChange={(cont) => setCont(k.frame, cont)} /></div>
                <button type="button" onClick={() => goOne(k)} disabled={!!baseReason} title={baseReason || `Generate frame #${k.frame} only`} data-testid={`cp-kf-generate-${k.frame}`}
                  className="mt-2 h-7 px-3 rounded-md bfx-gradient-bg text-[11px] font-semibold text-white flex items-center gap-1 disabled:opacity-40 shrink-0">
                  <Wand2 className="w-3.5 h-3.5" />Generate{one != null && <Money usd={one} className="font-mono text-[10px] opacity-90" />}</button>
              </div>
            </KeyframePrompt>
          ))}
        </div>
        <GenerateButton usd={usd} onClick={go} disabledReason={reason} label="Generate All" testid="cp-image-generate" className="w-full bfx-generate" />
        <p className="text-[11px] text-zinc-500 -mt-2">The window closes and {v.noun}s keep creating in the background — you can move to any section.</p>
        <Candidates jobs={st.image_jobs} onUse={onUse} nameFor={nameFor} onDismiss={(j) => patch((s) => ({ image_jobs: (s.image_jobs || []).filter((x) => x.id !== j.id) }))} />
      </DialogContent>
    </Dialog>
  );
}
